/**
 * Signature vs Custom Mode Selector
 * Step 1.5 — After sport selection, before configuration
 *
 * "Trust the system — we've already tuned this to the optimal state."
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Sparkles, SlidersHorizontal, Play, Lock, Info, X } from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const SignatureSelector = ({
  sport,
  sportLabel,
  signatureData,
  onSelectSignature,
  onSelectCustom,
  onBack,
}) => {
  const hasSignatureAudio = signatureData?.has_audio;
  const arc = signatureData?.arc || [];
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  return (
    <motion.div
      key="signature-selector"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex-1 flex flex-col p-6"
      data-testid="signature-custom-selector"
    >
      <button
        onClick={onBack}
        className="text-white/60 hover:text-white mb-8 self-start"
        data-testid="signature-back-btn"
      >
        <ArrowLeft size={24} />
      </button>

      <motion.div variants={itemVariants} className="mb-3">
        <p className="text-orange-400/70 text-xs uppercase tracking-[0.25em] mb-2">
          {sportLabel}
        </p>
        <h2
          className="text-2xl text-white mb-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Choose Your Mode
        </h2>
      </motion.div>

      <div className="space-y-4 mt-6">
        {/* Signature Mode */}
        <motion.button
          variants={itemVariants}
          onClick={hasSignatureAudio ? onSelectSignature : undefined}
          disabled={!hasSignatureAudio}
          className="w-full text-left relative overflow-hidden rounded-2xl transition-all group"
          style={{
            background: hasSignatureAudio
              ? "linear-gradient(145deg, rgba(251,191,36,0.12) 0%, rgba(20,16,12,0.95) 50%, rgba(234,179,8,0.06) 100%)"
              : "linear-gradient(145deg, rgba(40,30,20,0.6) 0%, rgba(20,16,12,0.95) 100%)",
            border: `1px solid ${hasSignatureAudio ? "rgba(251,191,36,0.25)" : "rgba(255,255,255,0.06)"}`,
            boxShadow: hasSignatureAudio
              ? "0 4px 40px rgba(251,191,36,0.08), 0 0 80px rgba(251,191,36,0.03) inset"
              : "none",
            opacity: hasSignatureAudio ? 1 : 0.5,
          }}
          whileTap={hasSignatureAudio ? { scale: 0.98 } : {}}
          data-testid="signature-mode-btn"
        >
          {hasSignatureAudio && (
            <motion.div
              className="absolute -top-8 -right-8 w-40 h-40 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(251,191,36,0.08) 0%, transparent 70%)" }}
              animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.05, 1] }}
              transition={{ duration: 5, repeat: 999, repeatType: "loop", ease: "easeInOut" }}
            />
          )}

          <div className="relative z-10 p-6">
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: hasSignatureAudio
                    ? "radial-gradient(circle, rgba(251,191,36,0.2) 0%, rgba(251,191,36,0.05) 100%)"
                    : "rgba(255,255,255,0.05)",
                }}
              >
                <Sparkles size={22} style={{ color: hasSignatureAudio ? "#FBBF24" : "rgba(255,255,255,0.2)" }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Signature
                  </h3>
                  <span
                    className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(251,191,36,0.12)", color: "rgba(251,191,36,0.7)", border: "1px solid rgba(251,191,36,0.15)" }}
                  >
                    Recommended
                  </span>
                </div>
                <p className="text-sm mb-3" style={{ fontFamily: "'Spectral', serif", fontStyle: "italic", color: "rgba(255,255,255,0.4)" }}>
                  Optimised for this performance state
                </p>

                {arc.length > 0 && (
                  <div className="flex items-center gap-1.5 mb-3">
                    {arc.map((phase, i) => (
                      <span key={i} className="flex items-center gap-1.5">
                        <span className="text-[10px] capitalize" style={{ color: "rgba(251,191,36,0.4)" }}>{phase}</span>
                        {i < arc.length - 1 && <span className="text-[8px]" style={{ color: "rgba(255,255,255,0.12)" }}>→</span>}
                      </span>
                    ))}
                  </div>
                )}

                {/* How this works — role=button div so we don't nest a <button> in <button> */}
                {hasSignatureAudio && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setShowHowItWorks(true); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowHowItWorks(true); } }}
                    className="inline-flex items-center gap-1.5 transition-opacity hover:opacity-80 cursor-pointer"
                    data-testid="how-it-works-btn"
                  >
                    <Info size={11} style={{ color: "rgba(251,191,36,0.35)" }} />
                    <span className="text-[10px] tracking-wide" style={{ color: "rgba(251,191,36,0.35)" }}>
                      How this works
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end mt-3">
              {hasSignatureAudio ? (
                <motion.div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.2)" }}
                  animate={{ boxShadow: ["0 0 10px rgba(251,191,36,0.05)", "0 0 20px rgba(251,191,36,0.12)", "0 0 10px rgba(251,191,36,0.05)"] }}
                  transition={{ duration: 3, repeat: 999, repeatType: "loop" }}
                >
                  <Play size={14} className="ml-0.5" style={{ color: "#FBBF24" }} />
                </motion.div>
              ) : (
                <div className="flex items-center gap-2">
                  <Lock size={12} style={{ color: "rgba(255,255,255,0.2)" }} />
                  <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.2)" }}>Signature track arriving soon</span>
                </div>
              )}
            </div>
          </div>
        </motion.button>

        {/* Custom Mode */}
        <motion.button
          variants={itemVariants}
          onClick={onSelectCustom}
          className="w-full text-left rounded-2xl transition-all group"
          style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
          whileTap={{ scale: 0.98 }}
          data-testid="custom-mode-btn"
        >
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.05)" }}>
                <SlidersHorizontal size={20} style={{ color: "rgba(255,255,255,0.35)" }} />
              </div>
              <div className="flex-1">
                <h3 className="text-base text-white/80 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Custom</h3>
                <p className="text-sm" style={{ fontFamily: "'Spectral', serif", fontStyle: "italic", color: "rgba(255,255,255,0.3)" }}>
                  Choose your own music, voice & affirmations
                </p>
              </div>
            </div>
          </div>
        </motion.button>
      </div>

      {signatureData?.tagline && (
        <motion.p variants={itemVariants} className="text-center mt-10 text-[12px] italic"
          style={{ fontFamily: "'Spectral', serif", color: "rgba(255,255,255,0.15)" }}
        >
          "{signatureData.tagline}"
        </motion.p>
      )}

      {/* How This Works Modal */}
      <AnimatePresence>
        {showHowItWorks && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-8"
            style={{ background: "rgba(10,8,6,0.92)" }}
            onClick={() => setShowHowItWorks(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-sm w-full rounded-2xl p-8"
              style={{ background: "rgba(30,24,18,0.95)", border: "1px solid rgba(251,191,36,0.1)" }}
              onClick={(e) => e.stopPropagation()}
              data-testid="how-it-works-modal"
            >
              <button
                onClick={() => setShowHowItWorks(false)}
                className="absolute top-4 right-4"
                style={{ color: "rgba(255,255,255,0.3)" }}
              >
                <X size={18} />
              </button>

              <Sparkles size={20} style={{ color: "rgba(251,191,36,0.5)" }} className="mb-5" />

              <h3 className="text-base mb-5" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.8)" }}>
                How Signature works
              </h3>

              <div className="space-y-4">
                {[
                  "Signature sessions are pre-optimised for your performance state.",
                  "Music, emotional arc, and affirmations are precisely placed to guide you into the zone.",
                  "You don't need to customise anything. Simply trust the process and enter the state.",
                ].map((text, i) => (
                  <p key={i} className="text-[13px] leading-relaxed"
                    style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.4)" }}
                  >
                    {text}
                  </p>
                ))}
              </div>

              <p className="text-[10px] mt-6" style={{ color: "rgba(251,191,36,0.25)" }}>
                Refined through performance state modelling
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};


/**
 * Signature Focus Area Selection
 * Step 1.6 — Same music, same arc, different affirmations
 * Includes optional ritual toggle and arrival transition
 */
const SignatureFocusSelector = ({
  sportLabel,
  signatureData,
  onSelectFocusArea,
  onBack,
}) => {
  const focusAreas = signatureData?.focus_areas || [];
  const arc = signatureData?.arc || [];
  const [ritualEnabled, setRitualEnabled] = useState(false);
  const [arriving, setArriving] = useState(false);
  const [pendingFocusArea, setPendingFocusArea] = useState(null);

  const handleFocusClick = (fa) => {
    if (!fa.has_audio) return;
    setPendingFocusArea(fa);
    setArriving(true);
    // Arrival transition — 3.5s soft fade
    setTimeout(() => {
      setArriving(false);
      onSelectFocusArea(fa, ritualEnabled);
    }, 3500);
  };

  return (
    <>
      <AnimatePresence>
        {arriving && (
          <motion.div
            key="arrival-transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: "#0a0f0a" }}
            data-testid="arrival-transition"
          >
            <motion.div className="text-center">
              <motion.div
                className="w-24 h-24 rounded-full mx-auto mb-8"
                style={{ background: "radial-gradient(circle, rgba(251,191,36,0.06) 0%, transparent 70%)" }}
                animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: 999, repeatType: "loop" }}
              />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 2 }}
                className="text-lg italic"
                style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.35)" }}
              >
                Arrive…
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        key="signature-focus-selector"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex-1 flex flex-col p-6"
        data-testid="signature-focus-selector"
      >
        <button onClick={onBack} className="text-white/60 hover:text-white mb-8 self-start" data-testid="focus-back-btn">
          <ArrowLeft size={24} />
        </button>

        <motion.div variants={itemVariants} className="mb-2">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} style={{ color: "rgba(251,191,36,0.5)" }} />
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "rgba(251,191,36,0.5)" }}>
              {sportLabel} Signature
            </p>
          </div>
          <h2 className="text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            What's your focus?
          </h2>
          <p className="text-sm" style={{ fontFamily: "'Spectral', serif", fontStyle: "italic", color: "rgba(255,255,255,0.25)" }}>
            Same environment. Different intention.
          </p>
        </motion.div>

        {arc.length > 0 && (
          <motion.div variants={itemVariants} className="flex items-center gap-1.5 mb-5 mt-2">
            {arc.map((phase, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <span className="text-[9px] capitalize" style={{ color: "rgba(251,191,36,0.25)" }}>{phase}</span>
                {i < arc.length - 1 && <span className="text-[7px]" style={{ color: "rgba(255,255,255,0.08)" }}>→</span>}
              </span>
            ))}
          </motion.div>
        )}

        {/* Optional ritual toggle */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-between rounded-xl px-4 py-3 mb-5"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }}
        >
          <span className="text-[12px]" style={{ color: "rgba(255,255,255,0.3)" }}>
            Add a quick pre-round ritual?
          </span>
          <button
            onClick={() => setRitualEnabled(!ritualEnabled)}
            className="w-10 h-5 rounded-full transition-all relative"
            style={{
              background: ritualEnabled ? "rgba(251,191,36,0.25)" : "rgba(255,255,255,0.08)",
            }}
            data-testid="ritual-toggle"
          >
            <motion.div
              className="absolute top-0.5 w-4 h-4 rounded-full"
              style={{ background: ritualEnabled ? "rgba(251,191,36,0.7)" : "rgba(255,255,255,0.3)" }}
              animate={{ left: ritualEnabled ? 22 : 2 }}
              transition={{ duration: 0.2 }}
            />
          </button>
        </motion.div>

        <div className="space-y-3">
          {focusAreas.map((fa) => (
            <motion.button
              key={fa.id}
              variants={itemVariants}
              onClick={() => handleFocusClick(fa)}
              disabled={!fa.has_audio || arriving}
              className="w-full text-left rounded-2xl transition-all"
              style={{
                background: fa.has_audio
                  ? "linear-gradient(145deg, rgba(251,191,36,0.06) 0%, rgba(20,16,12,0.9) 100%)"
                  : "rgba(255,255,255,0.02)",
                border: `1px solid ${fa.has_audio ? "rgba(251,191,36,0.12)" : "rgba(255,255,255,0.04)"}`,
                opacity: fa.has_audio ? 1 : 0.4,
              }}
              whileTap={fa.has_audio ? { scale: 0.98 } : {}}
              data-testid={`focus-area-${fa.id}`}
            >
              <div className="p-5 flex items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-sm text-white/90 mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {fa.name}
                  </h3>
                  <p className="text-[11px] italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(251,191,36,0.3)" }}>
                    {fa.subtitle}
                  </p>
                </div>
                {fa.has_audio ? (
                  <Play size={14} style={{ color: "rgba(251,191,36,0.4)" }} />
                ) : (
                  <Lock size={12} style={{ color: "rgba(255,255,255,0.15)" }} />
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>
    </>
  );
};


export default SignatureSelector;
export { SignatureFocusSelector };
